import {
  resolveXingshanConfigurationRowForDevice,
  xingshanHidMatchesConfiguration,
  isKeyboardFirmwareUpgradeInProgress,
  runSerializedXingshanSdkOp,
  stampOtherMenuOnlyIfGenericXingshanHid,
} from "@eweadn-qiankun/shared-utils";
import { getServiceKeyboard } from "../../../sdkApi/runtime";
import type { SdkConnectContext } from "../types";
import { deviceOperationLock } from "../locks";
import { xingshanCardDedupeKey } from "../helpers";
import { startXingshanDeviceSessionPresence } from "../../deviceSessionPresence";

const XINGSHAN_HID_SETTLE_MS = 120;

/**
 * 从星闪 SDK getDevices() 结果中解析与配置表行对应的设备实例。
 * 多设备在线时禁止盲目取 [0]，避免串台。
 */
export function resolveXingshanDeviceFromList(
  xsDevice: any[],
  configuration: any
): any | null {
  if (!Array.isArray(xsDevice) || xsDevice.length === 0 || !configuration) {
    return null;
  }
  const matched = xsDevice.filter((it) =>
    xingshanHidMatchesConfiguration(configuration, it)
  );
  if (matched.length === 1) return matched[0];
  if (matched.length > 1) return matched[0];
  return null;
}

function isXingshanDeviceStateBusyError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "InvalidStateError") {
    return true;
  }
  const message = String(error ?? "");
  return (
    message.includes("InvalidStateError") ||
    message.includes("operation that changes the device state is in progress")
  );
}

/** 关闭星闪 HID：并发 close/open 时短暂等待后重试，避免 InvalidStateError */
async function safeCloseXingshanDevice(): Promise<void> {
  const keyboard = getServiceKeyboard();
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      await keyboard.closeDevice();
      return;
    } catch (error) {
      if (!isXingshanDeviceStateBusyError(error) || attempt >= 2) {
        console.warn("[xingshan] closeDevice 失败:", error);
        return;
      }
      await new Promise((resolve) =>
        setTimeout(resolve, XINGSHAN_HID_SETTLE_MS * (attempt + 1))
      );
    }
  }
}

async function initXingshanSdkForConfigurationInner(
  configuration: any,
  options?: { skipPresenceRestart?: boolean }
): Promise<boolean> {
  if (isKeyboardFirmwareUpgradeInProgress()) return false;
  if (!configuration || configuration.solutionRovider !== "xingshan") {
    return false;
  }

  await safeCloseXingshanDevice();
  const xsDevice = await getServiceKeyboard().getDevices();
  const resolved = resolveXingshanDeviceFromList(xsDevice ?? [], configuration);
  const nId = resolved?.id;
  if (nId == null) return false;

  try {
    await getServiceKeyboard().init(nId);
  } catch (error) {
    if (!isXingshanDeviceStateBusyError(error)) {
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, XINGSHAN_HID_SETTLE_MS));
    await getServiceKeyboard().init(nId);
  }

  if (!options?.skipPresenceRestart && isXingshanDriverSessionActive()) {
    void startXingshanDeviceSessionPresence();
  }
  return true;
}

/**
 * 将星闪 SDK 当前实例绑定到指定 configuration（与设备卡片 / equipmentData 一致）。
 * 用于多键盘场景下纠正「最后一次 init 覆盖」导致的串台。
 */
export async function initXingshanSdkForConfiguration(
  configuration: any,
  options?: { skipPresenceRestart?: boolean }
): Promise<boolean> {
  return runSerializedXingshanSdkOp(() =>
    initXingshanSdkForConfigurationInner(configuration, options)
  );
}

/** 与 deviceSession.isDeviceSessionActive 一致；勿 import deviceSession 避免循环依赖 */
function isXingshanDriverSessionActive(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.sessionStorage.getItem("eweadn:session:isConnecting") === "true"
    );
  } catch {
    return false;
  }
}

/** 多星闪键盘切换：已在 connectList 时仍须 init 到当前配置对应设备，否则 SDK 停在上一台 */
export async function runXingshanConnect(
  ctx: SdkConnectContext,
  args: {
    device: any;
    configuration: any;
    isRetry?: boolean;
    /** 用户点击「添加新设备」等主动连接时为 true，重复卡片时弹 message */
    hintIfDuplicate?: boolean;
  }
): Promise<void> {
  if (isKeyboardFirmwareUpgradeInProgress()) return;
  const {
    device,
    configuration,
    isRetry = false,
    hintIfDuplicate = false,
  } = args;
  try {
    await runSerializedXingshanSdkOp(async () => {
      await safeCloseXingshanDevice();
      const xsDevice = await getServiceKeyboard().getDevices();
      if (xsDevice && xsDevice.length) {
        const resolved = resolveXingshanDeviceFromList(xsDevice, configuration);
        const nId = resolved?.id ?? null;
        const rowForCard =
          (resolved && resolveXingshanConfigurationRowForDevice(resolved)) ||
          configuration;
        const cardConfiguration =
          stampOtherMenuOnlyIfGenericXingshanHid(
            rowForCard,
            resolved?.productName ?? device?.productName
          ) ?? rowForCard;
        const lineKey = xingshanCardDedupeKey(cardConfiguration);
        let existence: boolean = false;
        ctx.connectListRef.current.forEach((item: any) => {
          if (nId != null && item?.device?.id === nId) {
            existence = true;
            return;
          }
          if (
            lineKey &&
            item?.configuration?.solutionRovider === "xingshan" &&
            xingshanCardDedupeKey(item.configuration) === lineKey
          ) {
            existence = true;
          }
        });
        if (hintIfDuplicate && !isRetry && existence) {
          ctx.notifyAlreadyConnected?.();
        } else if (nId != null) {
          await getServiceKeyboard().init(nId);
          ctx.devicesKeyRef.current = null;
          ctx.setIsConnecting(true);
          void startXingshanDeviceSessionPresence();
          if (!existence && !isRetry) {
            ctx.setConnectList((prevList) => [
              ...prevList,
              {
                device: resolved,
                deviceInfo: null,
                configuration: cardConfiguration,
              },
            ]);
          }
        }
      } else {
        deviceOperationLock.xingshan.unlock();
        if (deviceOperationLock.xingshan.number < 10) {
          deviceOperationLock.xingshan.connectNumber();
          // WebHID 刚授权时 SDK 枚举常滞后，须间隔重试而非同步连打
          await new Promise((r) => setTimeout(r, 300));
          await runXingshanConnect(ctx, {
            device,
            configuration,
            isRetry: true,
            hintIfDuplicate,
          });
        }
        console.log(
          `星闪设备连接失败，正在重试第${deviceOperationLock.xingshan.number}`
        );
      }
    });
  } catch (error) {
    deviceOperationLock.xingshan.unlock();
    if (deviceOperationLock.xingshan.number < 10) {
      deviceOperationLock.xingshan.connectNumber();
      await new Promise((r) => setTimeout(r, 300));
      await runXingshanConnect(ctx, {
        device,
        configuration,
        isRetry: true,
        hintIfDuplicate,
      });
    }
    console.log(
      `星闪设备连接失败，正在重试第${deviceOperationLock.xingshan.number}`,
      error
    );
  }
}
